/* eslint-disable @typescript-eslint/no-non-null-assertion */
/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { PrivateRoutePath } from 'routes/paths';
import { useDispatch, useSelector } from 'react-redux';
import * as PinDuck from 'store/ducks/settings/managePin';
import { Ducks, ParentalRatingValue } from '@wit/vtv-x-foundation-lib';
import ParentalControl from 'core/services/core/ParentalControl';
import * as ParentalControlDuck from 'core/store/ducks/parentalControl';
import { Modal, Pin, SimpleHeader, OptionsButtons, Spinner, Button } from '../../components';
import translate from '../../../services/i18n/translate';
import { EOptionType, IOption } from '../../../types/components/Settings';
import { ButtonType } from '../../components/Button';

const ParentalControlPage: React.FunctionComponent = () => {
  const dispatch = useDispatch();

  const ageRatingOff: MutableRefObject<ParentalRatingValue> = useRef({
    ratingValue: 0,
    label: translate.buttonOff,
    selectable: true,
    options: {},
  });
  const ageRatingOffArray: MutableRefObject<ParentalRatingValue[]> = useRef([ageRatingOff.current]);
  const ageRatingAskPinDuck = useSelector(ParentalControlDuck.Selectors.getAgeRatingAskPin);

  // Dates on Text
  const parentalDMS = useSelector(Ducks.Dms.Selectors.getParentalControl);
  const start = parentalDMS?.watershed?.startTime.toString();
  const end = parentalDMS?.watershed?.endTime.toString();
  // Only show text description if defaultON is true
  const defaultON = parentalDMS?.watershed?.defaultON || false;

  // On entering or leaving the page, force to ask Pin, and reset Pin states
  // Leaving the dependency array empty, it only will run on component mount and unmount
  useEffect(() => {
    dispatch(ParentalControlDuck.ActionCreators.setAgeRatingAskPin(true));
    dispatch(PinDuck.ActionCreators.pinReset());
    return () => {
      dispatch(ParentalControlDuck.ActionCreators.setAgeRatingAskPin(true));
      dispatch(PinDuck.ActionCreators.pinReset());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Defines the title and backLink
  const prepareHeaderProps = {
    subtitle: translate['setting.age.ratings.title'],
    backLabel: translate['menu.profile.settings'],
    backUrl: PrivateRoutePath.SETTINGS,
  };

  // Get User preferences
  const defaultUser = useSelector(Ducks.Household.Selectors.data.getDefaultUser);

  // State for Loading
  const [isLoadingPurchasesPin, setIsLoadingPurchasesPin] = useState<boolean>(false);
  const [isLoadingChangeRating, setIsLoadingChangeRating] = useState<boolean>(false);

  // State for action
  const [activeAction, setActiveAction] = useState<EOptionType>();

  // Updating Store States
  const isLoading = useSelector(ParentalControlDuck.Selectors.isProcessing);
  const isComplete = useSelector(ParentalControlDuck.Selectors.isProcessed);

  // #region PARENTAL RATING

  // PARENTAL RATING - Set Value to send to Duck
  const [ratingOptionSelected, setRatingOptionSelected] = useState<number>(-1);
  // PARENTAL RATING - Set the state if Parental Validation is active/inactive
  const [isParentalValidationActive, setIsParentalValidationActive] = useState<boolean>(false);
  // PARENTAL RATING - State for Pin Dialog
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  // PARENTAL RATING - Get Active Option
  const activeOption = ParentalControl.UI.getActiveRating() || ageRatingOff.current;

  // PARENTAL RATING - Loading Spinner
  useEffect(() => {
    if (activeAction === EOptionType.AGE_RATING) {
      setIsLoadingChangeRating(isLoading);
    }
    if (isComplete) {
      setIsLoadingChangeRating(false);
    }
  }, [isLoading, activeAction, isComplete]);

  // PARENTAL RATING - Activate/Deactivate Parental Control
  useEffect(() => {
    if (defaultUser?.parentalValidation !== undefined) {
      setIsParentalValidationActive(defaultUser?.parentalValidation);
    }
  }, [defaultUser?.parentalValidation]);

  // PARENTAL RATING - Get All Options
  const allParentalOptions = useSelector(Ducks.Dms.Selectors.getParentalControl);
  const [allOptionButtons, setAllOptionButtons] = useState<ParentalRatingValue[]>([]);
  useEffect(() => {
    if (allParentalOptions?.parentalRating.parentalRatingValues) {
      const options = allParentalOptions?.parentalRating.parentalRatingValues;
      const sortedOptions = options.slice().sort((a: ParentalRatingValue, b: ParentalRatingValue) => {
        if (a.ratingValue > b.ratingValue) {
          return -1;
        }
        if (a.ratingValue < b.ratingValue) {
          return 1;
        }
        return 0;
      });
      const allOptions: ParentalRatingValue[] = ageRatingOffArray.current.concat(sortedOptions);
      setAllOptionButtons(allOptions);
    }
  }, [ageRatingOffArray, allParentalOptions]);

  // PARENTAL RATING - PIN VALIDATION
  const pinIsCorrectDuck = useSelector(PinDuck.Selectors.getPinIsCorrect);
  const [isPinCorrect, setIsPinCorrect] = useState<boolean>(false);
  useEffect(() => {
    if (pinIsCorrectDuck !== undefined) {
      setIsPinCorrect(pinIsCorrectDuck);
    } else {
      setIsPinCorrect(false);
    }
  }, [pinIsCorrectDuck]);

  const setParentalOption = useCallback(
    (option: ParentalRatingValue) => {
      setRatingOptionSelected(option.ratingValue);
      if (ageRatingAskPinDuck) {
        setOpenDialog(true);
      }
      return null;
    },
    [ageRatingAskPinDuck]
  );

  useEffect(() => {
    if (isPinCorrect === true && activeAction === EOptionType.AGE_RATING) {
      setOpenDialog(false);
      dispatch(ParentalControlDuck.ActionCreators.setAgeRatingAskPin(false));
      if (ratingOptionSelected >= 0) {
        // Activate Parental Control and
        if (ratingOptionSelected > 0 && !isParentalValidationActive) {
          dispatch(ParentalControlDuck.ActionCreators.changeParentalValidation(true));
        }

        // If value is zero, then it is disabled parentalControl
        if (ratingOptionSelected === 0 && isParentalValidationActive) {
          dispatch(ParentalControlDuck.ActionCreators.changeParentalValidation(false));
        }

        // Set the AgeRating value selected
        dispatch(ParentalControlDuck.ActionCreators.setAgeRating(ratingOptionSelected));
      }
    }
  }, [isPinCorrect, ratingOptionSelected, dispatch, isParentalValidationActive, activeAction]);

  // List of all Age Rating
  const renderAgeRatingList = useCallback(() => {
    return allOptionButtons.map((item: ParentalRatingValue) => {
      if (item.selectable) {
        return (
          <Button
            active={activeOption?.ratingValue === item.ratingValue}
            onClick={() => {
              setParentalOption(item);
              setActiveAction(EOptionType.AGE_RATING);
            }}
            buttonType={ButtonType.LI}
            text={item.label}
            key={item.ratingValue}
            bemBlock="ParentalControlSettings"
            bemModifier={item.ratingValue}
          />
        );
      }
      return null;
    });
  }, [activeOption, allOptionButtons, setParentalOption]);

  // #endregion PARENTAL RATING

  // #region PURCHASE PIN
  // PURCHASE PIN - Modal Message warning the user
  // that the ParentalControl has to be enabled to change Purchase Pin
  const [showDisabledPurchasePinMessage, setShowDisabledPurchasePinMessage] = useState<boolean>(false);

  // PURCHASE PIN - Loading Effects
  useEffect(() => {
    if (activeAction === EOptionType.PURCHASE_PIN) {
      if (isLoading) {
        setIsLoadingPurchasesPin(true);
      }
      if (isComplete) {
        setIsLoadingPurchasesPin(false);
      }
    }
  }, [isParentalValidationActive, isComplete, isLoading, activeAction]);

  // PURCHASE PIN - Defines the buttons present in the Options
  const [optionButtons, setOptionButtons] = useState<IOption[]>([]);

  useEffect(() => {
    // for the purchase pin to be enabled, both master pin and purchase pin must be enabled in the default user
    const purchasePinEnabled = defaultUser?.parentalValidation && defaultUser?.purchasePinEnabled;
    setOptionButtons([
      {
        text: translate.buttonOff,
        key: 'purchasePinOff',
        value: false,
        type: EOptionType.PURCHASE_PIN,
        isSelected: purchasePinEnabled === false,
        disabled: false,
      },
      {
        text: translate.buttonOn,
        key: 'purchasePinOn',
        value: true,
        type: EOptionType.PURCHASE_PIN,
        isSelected: purchasePinEnabled === true,
        disabled: false,
      },
    ]);
  }, [defaultUser?.parentalValidation, defaultUser?.purchasePinEnabled]);

  const [optionPurchasePinSelected, setOptionPurchasePinSelected] = useState<IOption>();

  const purchasePinOption = useCallback(
    (option: IOption) => {
      setActiveAction(EOptionType.PURCHASE_PIN);
      setOptionPurchasePinSelected(option);
      if (!option?.disabled) {
        if (ageRatingAskPinDuck && isParentalValidationActive) {
          setOpenDialog(true);
        }
        if (!isParentalValidationActive) {
          setShowDisabledPurchasePinMessage(true);
        }
      }
      return null;
    },
    [ageRatingAskPinDuck, isParentalValidationActive]
  );

  // PURCHASE PIN - Dispatch selected option
  useEffect(() => {
    if (isPinCorrect === true && activeAction === EOptionType.PURCHASE_PIN) {
      dispatch(ParentalControlDuck.ActionCreators.setAgeRatingAskPin(false));
      if (isParentalValidationActive && optionPurchasePinSelected?.value !== undefined) {
        setOpenDialog(false);
        dispatch(ParentalControlDuck.ActionCreators.changePurchasePin(optionPurchasePinSelected.value === true));
      } else {
        setShowDisabledPurchasePinMessage(true);
      }
    }
  }, [activeAction, dispatch, isParentalValidationActive, isPinCorrect, optionPurchasePinSelected?.value]);

  const onCloseDisabledPurchasePinMessage = () => {
    setShowDisabledPurchasePinMessage(false);
  };
  // #endregion

  /* MODAL Related */
  const onCloseModal = () => {
    setOpenDialog(false);
  };

  return (
    <div
      className="template-holder back-nav component-inner component-inner-sub-settings"
      id="Settings_parentalControl"
    >
      <Modal setIsShowing={setOpenDialog} isShowing={openDialog}>
        <Pin closeModal={onCloseModal} />
      </Modal>
      <Modal
        setIsShowing={setShowDisabledPurchasePinMessage}
        isShowing={showDisabledPurchasePinMessage}
        title={translate['modal.title.information']}
        message={translate['setting.purchases.activate.pin']}
        buttons={[{ id: 'ok', text: translate['modal.button.ok'], onClick: onCloseDisabledPurchasePinMessage }]}
      />
      <SimpleHeader
        subtitle={prepareHeaderProps.subtitle}
        backLabel={prepareHeaderProps.backLabel}
        backUrl={prepareHeaderProps.backUrl}
      />
      <div className="setting-container" style={{ position: 'relative' }}>
        {defaultON && start && end ? (
          <p className="description">
            {translate.formatString(translate.parentalControlsInfoStartEnd, { start, end }).toString()}
          </p>
        ) : null}
        <div className="container-options">
          <ul>{renderAgeRatingList()}</ul>
        </div>
        <Spinner isActive={isLoadingChangeRating} isEmbedded={false} />
      </div>
      <div className="setting-separator" />
      <div className="setting-container">
        <h2>{translate['setting.purchases.title']}</h2>
        <p className="description">{translate['setting.purchases.description']}</p>
        <div className="container-options playback">
          <OptionsButtons
            options={optionButtons}
            onClickCallback={purchasePinOption}
            bemBlock="ParentalControlSettings"
          />
        </div>
        <div className="spinner-wrapper">
          <Spinner isActive={isLoadingPurchasesPin} isEmbedded={false} />
        </div>
      </div>
    </div>
  );
};

export default ParentalControlPage;
